import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  XIcon,
} from './icons';

/**
 * Multi-step add-flow primitive (card 059).
 *
 * Modal-overlay wizard that handles step navigation, per-step
 * validation, draft persistence, focus trapping, and keyboard
 * shortcuts so individual flows (add webhook, mint key, create
 * project, …) only have to describe the steps.
 *
 * Shape (parent file):
 *
 *   const FIELDS = { name: '', url: '' };
 *   <Wizard
 *     id="webhook-create"            // sessionStorage key
 *     title="Add a webhook"
 *     initialValues={FIELDS}
 *     sensitiveFields={['secret']}    // skip these in the draft
 *     onCancel={close}
 *     onSubmit={async (values) => { … }}
 *   >
 *     <WizardStep title="Name + event" isValid={(v) => !!v.name}>
 *       {({ values, set }) => (
 *         <>
 *           <input value={values.name} onChange={…} />
 *           …
 *         </>
 *       )}
 *     </WizardStep>
 *     <WizardStep title="Endpoint" isValid={(v) => isHttpsUrl(v.url)}>…</WizardStep>
 *     <WizardStep title="Review">{ReviewBody}</WizardStep>
 *   </Wizard>
 *
 * The render-prop on each step receives the shared state
 * (`{ values, set, setMany, busy, error, goNext }`) so steps don't
 * have to wire their own state.  `useWizard()` is also exported for
 * cases where a deeply nested input needs the bag without prop drilling.
 *
 * Behaviour notes:
 *
 *  - Esc cancels (calls onCancel). Cmd/Ctrl-Enter advances on
 *    intermediate steps and submits on the last step.
 *  - Click outside the modal is ignored — drafts only commit when
 *    the operator hits Cancel or Submit.
 *  - Focus trap: Tab + Shift-Tab cycle through the modal's focusable
 *    children.  We re-evaluate on each Tab press so async-loaded
 *    content (eg fetch'd scope catalogue) joins the trap automatically.
 *  - Draft persistence: sessionStorage key `wizard:<id>`.  Sensitive
 *    fields (signing secrets, plaintext keys) are stripped before
 *    write.  Refresh mid-flow rehydrates the values + last step
 *    index; submit/cancel clears it.
 *  - aria-live region in the header announces the active step so
 *    screen readers don't lose position when Next/Back is pressed.
 *
 * Wizard never owns the network call — `onSubmit(values)` is async
 * and the wizard only manages busy/error state around it.
 */

const WizardCtx = createContext(null);

export function useWizard() {
  const ctx = useContext(WizardCtx);
  if (!ctx) {
    throw new Error('useWizard() must be called inside <Wizard>');
  }
  return ctx;
}

export function Wizard({
  id,
  title,
  subtitle,
  initialValues = {},
  sensitiveFields = [],
  onCancel,
  onSubmit,
  submitLabel = 'Create',
  children,
}) {
  // Steps are declared as <WizardStep> children so render order is
  // the canonical step order.  We strip falsy entries (`{cond && <WizardStep…>}`)
  // and capture each one's metadata + render prop once.
  const steps = useMemo(() => {
    const out = [];
    Children.forEach(children, (child) => {
      if (!isValidElement(child)) return;
      if (child.type !== WizardStep) return;
      out.push({
        title: child.props.title,
        // `isValid(values)` returns truthy when Next is allowed.  Steps
        // without one are always valid.
        isValid: child.props.isValid || (() => true),
        // `optional: true` flips the Next label to "Skip" when the step
        // is currently invalid — used by the test-ping step.
        optional: !!child.props.optional,
        body: child.props.children,
      });
    });
    return out;
  }, [children]);

  const stepCount = steps.length;

  // Hydrate from sessionStorage on first mount — refresh mid-flow
  // shouldn't drop progress.  Sensitive fields are always re-seeded
  // from `initialValues` so a stale draft never carries a leaked
  // secret across reloads.
  const [values, setValues] = useState(() => {
    const seed = { ...initialValues };
    if (!id || typeof window === 'undefined') return seed;
    try {
      const raw = window.sessionStorage.getItem(`wizard:${id}`);
      if (!raw) return seed;
      const parsed = JSON.parse(raw);
      const draft = parsed?.values || {};
      for (const k of sensitiveFields) delete draft[k];
      return { ...seed, ...draft };
    } catch {
      return seed;
    }
  });

  const [stepIdx, setStepIdx] = useState(() => {
    if (!id || typeof window === 'undefined') return 0;
    try {
      const raw = window.sessionStorage.getItem(`wizard:${id}`);
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      const idx = Number.isInteger(parsed?.stepIdx) ? parsed.stepIdx : 0;
      return Math.max(0, Math.min(idx, Math.max(0, stepCount - 1)));
    } catch {
      return 0;
    }
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Persist on every change.  Strip sensitive fields before writing so
  // a one-time signing secret never lands in sessionStorage.
  useEffect(() => {
    if (!id || typeof window === 'undefined') return;
    try {
      const safe = { ...values };
      for (const k of sensitiveFields) delete safe[k];
      window.sessionStorage.setItem(
        `wizard:${id}`,
        JSON.stringify({ values: safe, stepIdx }),
      );
    } catch { /* quota or private-mode — drafts are non-essential */ }
    // sensitiveFields is captured by identity per render — the consumer
    // passes a stable literal in 99 % of cases.  Adding it to deps
    // would clear the draft on hot-reload churn for no real win.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, values, stepIdx]);

  const clearDraft = useCallback(() => {
    if (!id || typeof window === 'undefined') return;
    try { window.sessionStorage.removeItem(`wizard:${id}`); } catch { /* noop */ }
  }, [id]);

  const set = useCallback((key, value) => {
    setValues((cur) => ({ ...cur, [key]: value }));
  }, []);

  const setMany = useCallback((patch) => {
    setValues((cur) => ({ ...cur, ...patch }));
  }, []);

  const active = steps[stepIdx];
  const isLast = stepIdx === stepCount - 1;
  const canAdvance = !!active && active.isValid(values);

  const goNext = useCallback(async () => {
    if (busy) return;
    if (!active) return;
    if (!isLast) {
      // Optional steps allow the operator to skip even when isValid
      // returns false (e.g. the "Send test ping" step where success
      // isn't required to save the subscription).
      if (!canAdvance && !active.optional) return;
      setError(null);
      setStepIdx((i) => Math.min(i + 1, stepCount - 1));
      return;
    }
    // Last step — submit.
    setBusy(true);
    setError(null);
    try {
      await onSubmit(values);
      clearDraft();
    } catch (ex) {
      setError(ex?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }, [busy, active, isLast, canAdvance, stepCount, onSubmit, values, clearDraft]);

  const goBack = useCallback(() => {
    if (busy) return;
    setError(null);
    setStepIdx((i) => Math.max(0, i - 1));
  }, [busy]);

  const cancel = useCallback(() => {
    if (busy) return;
    clearDraft();
    onCancel?.();
  }, [busy, clearDraft, onCancel]);

  // Body scroll lock — mount-only side effect so other modals on the
  // page don't race us.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Keyboard shortcuts + focus trap.  We attach to the modal root so
  // the events are scoped — nothing leaks to the underlying page.
  const rootRef = useRef(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        goNext();
        return;
      }
      if (e.key === 'Tab') {
        // Focus trap — keep Tab inside the modal.
        const focusables = node.querySelectorAll(FOCUSABLE_SELECTOR);
        const list = Array.from(focusables).filter(
          (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
        );
        if (list.length === 0) {
          e.preventDefault();
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [cancel, goNext]);

  // Move focus to the first focusable child whenever the active step
  // changes so the operator lands on the first input.  We skip the
  // initial render if the parent already focused something inside
  // (rare; defensive).
  useEffect(() => {
    if (!rootRef.current) return;
    const focusables = rootRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
    const first = Array.from(focusables).find(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
    );
    if (first && !rootRef.current.contains(document.activeElement)) {
      first.focus();
    }
  }, [stepIdx]);

  const ctx = useMemo(
    () => ({ values, set, setMany, busy, error, goNext, goBack, cancel, stepIdx, stepCount }),
    [values, set, setMany, busy, error, goNext, goBack, cancel, stepIdx, stepCount],
  );

  if (stepCount === 0) {
    // Defensive — a Wizard with no steps is a developer error, not a
    // user-facing one.  Render nothing rather than a half-broken modal.
    return null;
  }

  return (
    <WizardCtx.Provider value={ctx}>
      <div
        className="wiz__backdrop"
        role="presentation"
        // Click-outside does NOT cancel — the operator might have
        // typed something they don't want to drop accidentally.  Esc
        // and the explicit Cancel button are the two exits.
      >
        <div
          className="wiz"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`wiz-${id || 'modal'}-title`}
          ref={rootRef}
        >
          <header className="wiz__header">
            <div className="wiz__header-top">
              <div>
                <div className="wiz__eyebrow" aria-live="polite">
                  Step {stepIdx + 1} of {stepCount} — {active.title}
                </div>
                <h2 id={`wiz-${id || 'modal'}-title`} className="wiz__title">
                  {title}
                </h2>
                {subtitle && <p className="wiz__subtitle">{subtitle}</p>}
              </div>
              <button
                type="button"
                className="wiz__close"
                onClick={cancel}
                disabled={busy}
                aria-label="Cancel"
                title="Cancel (Esc)"
              >
                <XIcon size={18} />
              </button>
            </div>
            <ol className="wiz__dots" aria-hidden="true">
              {steps.map((s, i) => (
                <li
                  key={i}
                  className={
                    'wiz__dot'
                    + (i === stepIdx ? ' wiz__dot--active' : '')
                    + (i < stepIdx ? ' wiz__dot--done' : '')
                  }
                  title={s.title}
                />
              ))}
            </ol>
          </header>

          <div className="wiz__body">
            {typeof active.body === 'function'
              ? active.body(ctx)
              : active.body}
          </div>

          {error && (
            <div className="wiz__error" role="alert">{error}</div>
          )}

          <footer className="wiz__footer">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={cancel}
              disabled={busy}
            >
              Cancel
            </button>
            <div className="wiz__footer-right">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={goBack}
                disabled={busy || stepIdx === 0}
              >
                <ArrowLeftIcon /> Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={goNext}
                disabled={busy || (!canAdvance && !(active.optional && !isLast))}
                title={
                  isLast
                    ? `${submitLabel} (Cmd-Enter)`
                    : 'Continue (Cmd-Enter)'
                }
              >
                {isLast ? (
                  <>
                    <CheckIcon /> {busy ? `${submitLabel}…` : submitLabel}
                  </>
                ) : (
                  <>
                    {!canAdvance && active.optional ? 'Skip' : 'Next'}
                    <ArrowRightIcon />
                  </>
                )}
              </button>
            </div>
          </footer>
        </div>
        <style>{WIZARD_CSS}</style>
      </div>
    </WizardCtx.Provider>
  );
}

/**
 * Step declaration.  Renders nothing on its own — the parent Wizard
 * walks the children to extract step metadata + render props.
 *
 * Props:
 *
 *   title    — short label for the dot tooltip + the "Step N of M —"
 *              eyebrow.
 *   isValid  — `(values) => boolean`, controls Next.  Default: always
 *              valid.
 *   optional — when true, an invalid step still advances via a "Skip"
 *              button.  Used by the test-ping step.
 *   children — either a render-prop fn `(ctx) => ReactNode` or a
 *              plain ReactNode.
 */
// eslint-disable-next-line no-unused-vars
export function WizardStep({ title, isValid, optional, children }) {
  return null;
}

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// ─── client-side helpers exported for the consumers ──────────

/**
 * SSRF / scheme guard for the webhook URL step.  Mirrors the backend's
 * `assert_safe_url` so an operator gets a useful error before round-
 * tripping through the API.  Server is still the authoritative gate.
 */
export function isSafePublicHttpsUrl(input) {
  if (!input) return false;
  let parsed;
  try {
    parsed = new URL(input);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host.endsWith('.localhost')) return false;
  // Reject bare IPs whose first octet is in the RFC1918 / link-local
  // ranges.  We don't try to be exhaustive — the backend does the
  // real check; this is just an early friendly nudge.
  const ipv4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4) {
    const [, a, b] = ipv4.map((n) => parseInt(n, 10));
    if (a === 10) return false;
    if (a === 127) return false;
    if (a === 0) return false;
    if (a === 169 && b === 254) return false; // link-local
    if (a === 172 && b >= 16 && b <= 31) return false;
    if (a === 192 && b === 168) return false;
  }
  // IPv6 link-local + loopback prefix.
  if (host.startsWith('[::1') || host.startsWith('[fe80')) return false;
  return true;
}

const WIZARD_CSS = `
  .wiz__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.72);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1000;
    padding: 48px 16px 16px;
    overflow-y: auto;
  }
  .wiz {
    position: relative;
    background: var(--surface-0, #ffffff);
    border-radius: 14px;
    width: 100%;
    max-width: 640px;
    box-shadow: 0 28px 56px -20px rgba(15, 23, 42, 0.45);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 64px);
  }
  .wiz__header {
    padding: 18px 20px 8px;
    border-bottom: 1px solid var(--border-1, #e2e8f0);
  }
  .wiz__header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  .wiz__eyebrow {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--brand-700, #4338ca);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .wiz__title {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-1, #0f172a);
    margin: 4px 0 0;
    line-height: 1.25;
  }
  .wiz__subtitle {
    font-size: 13px;
    color: var(--text-3, #64748b);
    margin: 4px 0 0;
  }
  .wiz__close {
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--text-3, #64748b);
    padding: 4px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .wiz__close:hover {
    background: var(--surface-1, #f1f5f9);
    color: var(--text-1, #0f172a);
  }
  .wiz__dots {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    gap: 6px;
  }
  .wiz__dot {
    flex: 1;
    height: 4px;
    background: var(--surface-2, #e2e8f0);
    border-radius: 999px;
    transition: background 160ms ease;
  }
  .wiz__dot--done { background: var(--brand-500, #6366f1); opacity: 0.5; }
  .wiz__dot--active { background: var(--brand-600, #4f46e5); }
  .wiz__body {
    padding: 18px 20px;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
  .wiz__error {
    margin: 0 20px;
    padding: 10px 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: var(--danger-700, #b91c1c);
    font-size: 13px;
  }
  .wiz__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 20px 18px;
    border-top: 1px solid var(--border-1, #e2e8f0);
  }
  .wiz__footer-right { display: inline-flex; gap: 8px; align-items: center; }

  /* Shared field layout for steps — keeps labels above inputs and the
     same vertical rhythm everywhere a wizard renders.  Steps that want
     a two-column grid can stack a .wiz-grid inside .wiz-stack. */
  .wiz-stack { display: flex; flex-direction: column; gap: 14px; }
  .wiz-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  @media (max-width: 520px) {
    .wiz-grid { grid-template-columns: 1fr; }
  }
  .wiz-hint {
    font-size: 12px;
    color: var(--text-3, #64748b);
    line-height: 1.4;
    margin: -6px 0 0;
  }
  .wiz-review {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 6px 14px;
    font-size: 13px;
  }
  .wiz-review dt {
    color: var(--text-3, #64748b);
    font-weight: 600;
  }
  .wiz-review dd { margin: 0; color: var(--text-1, #0f172a); word-break: break-word; }
`;
